import { useState, useEffect, useCallback } from "react";
import Button from "../../../components/controls/button/Button";
import { Avatar } from "@fluentui/react-components";
import { calculatePageSize, countries } from "../../../helpers/utils";
import InputUnderline from "../../../components/controls/input/InputUnderline";
import service from "../../../store/services/clients.service";
import industryService from "../../../store/services/industry.service";
import Input from "../../../components/controls/input/customInput/CustomInput";
import CustomSelect from "../../../components/controls/select/CustomSelect";
import DropDown from "../../../components/controls/dropdown/DropdownFluent";
import { UploadCloudIcon, AddIconSvg, CalendarIcon } from "../../../modules/default-svg";
import { URL_UPDATE_CLIENT_LOGO } from "../../../store/services/URL";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";
import ContactsSelect from "../../../components/contactsSelect/ContactsSelect";
import { ColumnConfigProvider } from "../../../components/controls/tables/ColumnConfigProvider";
import DeleteRequest from "../../../components/DeleteRequest/DeleteRequest";
import Table from "../../../components/controls/tables/Table";
import RichTextEditor from "../../../components/richTextEditor/RichTextEditor";
import EntityFileUpload from "../../../components/controls/fileUpload/EntityFileUpload";
import styles from "./Clients.module.css";
import { Trash2 } from "lucide-react";

const Clients = (props) => {
  const {
    modalLoading,
    pageLoading,
    pagination,
    createClientRequest,
    updateClientRequest,
    allClients,
    getClientsRequest,
    error,
  } = props;
  const permissions = localStorage.getItem("permissions");
  const [isModalLoading, setIsModalLoading] = useState(false);

  useEffect(() => {
    setIsModalLoading(modalLoading);
  }, [modalLoading]);
  const [data, setData] = useState([]);
  const [pageNum, setPageNum] = useState(1);
  const [perPageItems] = useState(calculatePageSize());
  const [name, setName] = useState("");
  const [manager, setManager] = useState(null);
  const [newManager, setNewManager] = useState(null);
  const [managerId, setManagerId] = useState("");
  const [refId, setRefId] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [mgntTeam, setMgntTeam] = useState("");
  const [newMgntTeam, setNewMgntTeam] = useState("");
  const [mgntTeamIds, setMgntTeamIds] = useState("");
  const [coreTeam, setCoreTeam] = useState("");
  const [newCoreTeam, setNewCoreTeam] = useState("");
  const [coreTeamIds, setCoreTeamIds] = useState("");
  const [businessUnit, setBusinessUnit] = useState("");
  const [contractStartDate, setContractStartDate] = useState("");
  const [contractEndDate, setContractEndDate] = useState("");
  const [focal, setFocal] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [avatarImage, setAvatarImage] = useState(null);
  const [logo, setLogo] = useState(null);
  const [integratorLogo, setIntegratorLogo] = useState(null);
  const [integratorLogoUrl, setIntegratorLogoUrl] = useState(null);
  const [integratorLogoLoading, setIntegratorLogoLoading] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isCreateNewRow, setIsCreateNewRow] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteRequestModal, setDeleteRequestModal] = useState(false);
  const [deleteRequestData, setDeleteRequestData] = useState({});
  const [deleteModal, setDeleteModal] = useState(null);
  const dispatch = useDispatch();
  const [formErrors, setFormErrors] = useState({
    name: "",
    managerId: "",
    endDate: "",
  });
  const [tableSearch, setTableSearch] = useState(null);

  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
    const payload = { page: pageNumber ?? pageNum, size: pageSize ?? perPageItems, search: forceClearSearch ? null : (searchTerm ?? tableSearch) };
    getClientsRequest(payload);
  };
  const handleAddRow = useCallback(() => {
    setIsCreateNewRow(true);
    fetchData(pagination.TotalPages ?? 1, perPageItems > 10 ? perPageItems - 5 : perPageItems - 3);
  }, []);

  const handleEndDate = (e) => {
    if (new Date(e) <= new Date(contractStartDate)) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        endDate: "End Date should be after Start Date",
      }));
    } else {
      setFormErrors({});
      setContractEndDate(e);
    }
  };

  const handleCreateClientImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setAvatarImage(reader.result); // Set the uploaded image as a data URL
      };
      reader.readAsDataURL(file);
      setLogo(file);
    }
  };

  const handleCreateIntegratorLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setIntegratorLogoUrl(reader.result);
      };
      reader.readAsDataURL(file);
      setIntegratorLogo(file);
    }
  };

  const handleUpdateIntegratorLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIntegratorLogoLoading(true);
      const reader = new FileReader();
      reader.onload = () => {
        setIntegratorLogoUrl(reader.result);
      };
      reader.readAsDataURL(file);

      const payload = new FormData();
      payload.append("File", file);
      payload.append("IsDelete", false);

      const token = localStorage.getItem("accessToken");
      let headers = {};
      if (token) {
        headers = {
          Authorization: `Bearer ${token.replace(/"/g, "")}`,
        };
      }
      const options = {
        method: "PUT",
        headers: headers,
        body: payload,
      };
      return fetch(`${URL_UPDATE_CLIENT_LOGO}${selectedRow.id}/integrator-logo`, options)
        .then(async (response) => {
          if (response.status === 400) {
            dispatch(
              generalActions.showToast({
                title: "File type not supported",
                type: "error",
              })
            );
          }
          if (response.status === 200) {
            dispatch(
              generalActions.showToast({
                title: "Integrator logo updated successfully",
                type: "success",
              })
            );
          }
        })
        .catch((error) => {
          console.error("Error:", error);
          dispatch(
            generalActions.showToast({
              title: "Failed to update integrator logo",
              type: "error",
            })
          );
        })
        .finally(() => {
          setIntegratorLogoLoading(false);
        });
    }
  };

  const handleDeleteIntegratorLogo = () => {
    if (!isEdit) {
      setIntegratorLogo(null);
      setIntegratorLogoUrl(null);
      return;
    }

    setIntegratorLogoLoading(true);
    const payload = new FormData();
    payload.append("IsDelete", true);

    const token = localStorage.getItem("accessToken");
    let headers = {};
    if (token) {
      headers = {
        Authorization: `Bearer ${token.replace(/"/g, "")}`,
      };
    }
    const options = {
      method: "PUT",
      headers: headers,
      body: payload,
    };
    return fetch(`${URL_UPDATE_CLIENT_LOGO}${selectedRow.id}/integrator-logo`, options)
      .then(async (response) => {
        if (response.status === 200) {
          setIntegratorLogoUrl(null);
          dispatch(
            generalActions.showToast({
              title: "Integrator logo deleted successfully",
              type: "success",
            })
          );
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        dispatch(
          generalActions.showToast({
            title: "Failed to delete integrator logo",
            type: "error",
          })
        );
      })
      .finally(() => {
        setIntegratorLogoLoading(false);
      });
  };

  const handleUpdateImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setAvatarImage(reader.result); // Set the uploaded image as a data URL
      };
      reader.readAsDataURL(file);
      const payload = new FormData();
      payload.append("file", file);
      const token = localStorage.getItem("accessToken");
      let headers = {};
      if (token) {
        headers = {
          Authorization: `Bearer ${token.replace(/"/g, "")}`,
        };
      }
      const options = {
        method: "PUT",
        headers: headers,
        body: payload,
      };
      return fetch(`${URL_UPDATE_CLIENT_LOGO}${selectedRow.id}/Logo`, options)
        .then(async (response) => {
          if (response.status === 400) {
            dispatch(
              generalActions.showToast({
                title: "File type not supported",
                type: "error",
              })
            );
          }
          if (response.status === 200) {
            dispatch(
              generalActions.showToast({
                title: "Logo updated successfully",
                type: "success",
              })
            );
          }
        })
        .then((data) => data)
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  };

  const formBody = () => {
    return (
      <>
        <div className="d-flex align-items-center mb-3">
          {avatarImage ? (
            <div className="clientLogoContainer relative flex flex-col items-center justify-center max-w-32 h-14 overflow-hidden pr-2">
              <img
                src={avatarImage}
                alt="Client Logo"
                className="max-w-full max-h-full object-contain"
              />

              <div className="upload-icon absolute bottom-1 right-1 bg-white rounded-full p-1 shadow-md cursor-pointer">
                <label htmlFor="avatarUpload" className="cursor-pointer">
                  <UploadCloudIcon />
                </label>
                <input
                  id="avatarUpload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleUpdateImageChange}
                />
              </div>
            </div>
          ) : (
            <Avatar
              className="me-20 ModalAvatarBadge"
              size={64}
              color="neutral"
              name={name}
              shape="square"
              image={""}
              badge={{
                icon: (
                  <div className="upload-icon">
                    <label htmlFor="avatarUpload">
                      <UploadCloudIcon />
                    </label>
                    <input
                      id="avatarUpload"
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={
                        isEdit
                          ? handleUpdateImageChange
                          : handleCreateClientImageChange
                      }
                    />
                  </div>
                ),
              }}
            />
          )}

          <InputUnderline
            inputUnderlinewd="w-100"
            isRequired
            placeholder="Add client name"
            value={name}
            handleChange={setName}
            error={formErrors.name}
            loading={isLoading}
          />
        </div>

        <div className={`${styles.integratorLogoSection} ${integratorLogoLoading ? styles.loading : ''}`}>
          {integratorLogoLoading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}></div>
            </div>
          )}

          <label className={styles.integratorLogoLabel}>Integrator Logo</label>
          <div className={styles.integratorLogoContainer}>
            {integratorLogoUrl ? (
              <>
                <div className={styles.logoImageWrapper}>
                  <label htmlFor="integratorLogoUploadEdit" className="w-100 h-100 d-flex align-items-center justify-content-center cursor-pointer m-0">
                    <img
                      src={integratorLogoUrl}
                      alt="Integrator Logo"
                    />
                  </label>
                  <input
                    id="integratorLogoUploadEdit"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={isEdit ? handleUpdateIntegratorLogoChange : handleCreateIntegratorLogoChange}
                    disabled={integratorLogoLoading}
                  />
                </div>
                <div className={styles.logoActions}>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={handleDeleteIntegratorLogo}
                    disabled={integratorLogoLoading}
                    title="Delete integrator logo"
                  >
                    <Trash2 />
                  </button>
                </div>
              </>
            ) : (
              <div className={`${styles.uploadPrompt} ${isLoading ? styles.disabled : ''}`}>
                <label htmlFor="integratorLogoUpload" className="cursor-pointer d-flex align-items-center gap-2 m-0">
                  <UploadCloudIcon />
                  <span className={styles.uploadPromptText}>Upload</span>
                </label>
                <input
                  id="integratorLogoUpload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={isEdit ? handleUpdateIntegratorLogoChange : handleCreateIntegratorLogoChange}
                  disabled={isLoading}
                />
              </div>
            )}
          </div>
        </div>

        {!isLoading && (
          <>
            <div className="ModalAvatarGroup">
              <div className="row align-items-end gx-3">
                <div className="col-12">
                  <label class="Input_label">Client Manager</label>
                  <ContactsSelect
                    parentType="Clients"
                    parentId={selectedRow.id}
                    isMultiSelect={false}
                    isNewParent={!isEdit}
                    confirmHandler={setManagerId}
                    submitNewContactHandler={setNewManager}
                    existingSelectedContacts={manager ? [...[], manager] : []}
                  />
                </div>
              </div>
            </div>
            <div className="ModalAvatarGroup">
              <div className="row align-items-end gx-3">
                <div className="col-12">
                  <label class="Input_label">Management Team</label>
                  <ContactsSelect
                    parentType="Clients"
                    parentId={selectedRow.id}
                    isNewParent={!isEdit}
                    confirmHandler={setMgntTeamIds}
                    submitNewContactHandler={(newContact) => {
                      setNewMgntTeam((prev) => [...prev, newContact]);
                    }}
                    existingSelectedContacts={mgntTeam}
                  />
                </div>
              </div>
            </div>
            <div className="ModalAvatarGroup">
              <div className="row align-items-end gx-3">
                <div className="col-12">
                  <label class="Input_label">Core Team</label>
                  <ContactsSelect
                    parentType="Clients"
                    parentId={selectedRow.id}
                    isNewParent={!isEdit}
                    confirmHandler={setCoreTeamIds}
                    submitNewContactHandler={(newContact) => {
                      setNewCoreTeam((prev) => [...prev, newContact]);
                    }}
                    existingSelectedContacts={coreTeam}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter Account ID"
              value={refId}
              type="text"
              handleChange={setRefId}
              label="Account ID"
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <CustomSelect
              label="Industry"
              SelectText="industry"
              multiselect={true}
              creatable={true}
              searchText={industrySearchHandler}
              options={industryOptions}
              defaultVal={industries.map((i) => ({ label: i.name, value: i.id }))}
              onChange={handleIndustriesChange}
              createOptionRequest={(name) => industryService.createIndustryRequest(name)}
              setCreated={handleIndustryCreated}
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter business unit"
              value={businessUnit}
              type="text"
              handleChange={setBusinessUnit}
              label="Business Unit"
              loading={isLoading}
            />
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={contractStartDate}
              maxDate={contractEndDate}
              type="date"
              handleChange={setContractStartDate}
              label="Contract Start Date"
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={contractEndDate}
              minDate={contractStartDate}
              type="date"
              handleChange={handleEndDate}
              label="Contract End Date"
              errorMessage={formErrors.endDate}
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              value={focal}
              placeholder="Enter focal"
              type="text"
              handleChange={setFocal}
              label="Focal"
              loading={isLoading}
            />
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <DropDown
              label="Country"
              placeholder="country"
              options={countries}
              defaultVal={country}
              onChange={setCountry}
              loading={isLoading}
            />
            {/* <Searchable label="Program" SelectText="program" options={programs} defaultVal={program} onChange={setProgram} /> */}
          </div>
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter city"
              value={city}
              type="text"
              handleChange={setCity}
              label="City"
              customType="alpha"
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter state & province"
              value={stateProvince}
              type="text"
              handleChange={setStateProvince}
              label="State & Province"
              customType="alpha"
              loading={isLoading}
            />
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter postal code"
              value={postalCode}
              type="text"
              handleChange={setPostalCode}
              label="Postal Code"
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter status"
              value={status}
              type="text"
              handleChange={setStatus}
              label="Enter Status"
              loading={isLoading}
            />
          </div>
        </div>
        <div className="mb-3">
          <RichTextEditor
            label="Additional Address"
            content={address}
            onChange={setAddress}
            loading={isLoading}
            limit={255}
          />
        </div>

        <div className="mb-3">
          <RichTextEditor
            label="Notes"
            content={notes}
            onChange={setNotes}
            loading={isLoading}
          />
        </div>

        <EntityFileUpload
          entityId={selectedRow.id}
          entityType="Client"
          attachments={attachments}
          onAttachmentsChange={() => {
            // Refresh client data to get updated attachments
            if (selectedRow.id) {
              service
                .fetchSingleClientDataRequest(selectedRow.id)
                .then((clientData) => {
                  setAttachments(clientData.attachments);
                })
                .catch((error) => {
                  console.error("Error fetching client data:", error);
                });
            }
          }}
          disabled={isLoading || !selectedRow.id}
        />
      </>
    );
  };

  const handleDelete = (selectedRow) => {
    if (!isDisable) {
      setIsModalLoading(true);
      const id = selectedRow.id;
      service
        .deleteClient(id)
        .then((response) => {
          if (response && !response.IsDeletionAllowed) {
            setDeleteRequestData(response);
            setDeleteRequestModal(true);
          } else {
            // localStorage.removeItem("selectedRow");
            fetchData();
            setDeletedId(id);
            dispatch(
              generalActions.showToast({
                title: "Client deleted successfully",
                type: "success",
              })
            );
          }
        })
        .catch((err) => {
          dispatch(
            generalActions.showToast({
              title: "Something went wrong. Please try again later.",
              type: "error",
            })
          );
        })
        .finally(() => {
          setSelectedRow({});
          setDeleteModal(false);
          setIsModalLoading(false);
        });
    }
  };

  const handleDeleteRequested = () => {
    fetchData();
  };

  const industrySearchHandler = (value) => {
    industryService
      .searchIndustriesRequest(value)
      .then((res) => {
        const transformed = (res || []).map((obj) => ({ label: obj.name, value: obj.id }));
        setIndustryOptions(transformed);
      })
      .catch(() => {
        setIndustryOptions([]);
      });
  };

  const handleIndustriesChange = (ids) => {
    const pool = [...industries, ...industryOptions.map((o) => ({ id: o.value, name: o.label }))];
    const selected = ids.map((id) => pool.find((item) => item.id === id)).filter(Boolean);
    setIndustries(selected);
  };

  const handleIndustryCreated = (created) => {
    setIndustries((prev) => [...prev, { id: created.value, name: created.label }]);
  };

  const handleSave = () => {
    if (!isDisable) {
      const errors = {};
      if (!name) errors["name"] = `Name Field is Required`;
      setFormErrors(errors);
      if (Object.keys(errors).length) {
        return;
      }

      const payload = {
        name,
        managerId: managerId,
        refId,
        address,
        MgntTeam: mgntTeamIds,
        coreTeam: coreTeamIds,
        businessUnit: businessUnit,
        focal: focal,
        city,
        stateProvince,
        postalCode: postalCode,
        country,
        status,
        notes,
        contractStartDate,
        contractEndDate,
      };



      if (!isEdit) {
        payload.newManager = newManager ? JSON.stringify(newManager) : "";
        payload.newMgntTeam = newMgntTeam ? JSON.stringify(newMgntTeam) : "";
        payload.newCoreTeam = newCoreTeam ? JSON.stringify(newCoreTeam) : "";
      }
      if (Object.keys(selectedRow).length > 0) {
        payload.industryIds = industries.map((i) => i.id);
        updateClientRequest({ id: selectedRow.id, type: "listing", payload });
      } else {
        let finalPayload = new FormData();
        for (const key in payload) {
          if (payload[key] !== undefined && payload[key] !== null) {
            finalPayload.append(key, payload[key]);
          }
        }
        if (logo) {
          finalPayload.append("Logo", logo);
        }
        if (integratorLogo) {
          finalPayload.append("IntegratorLogo", integratorLogo);
        }

        if (industries) {
          industries.forEach(item => {
            finalPayload.append('industryIds', item.id);
          });
        }

        createClientRequest({ finalPayload });
      }
    }
  };

  const handleCancel = () => {
    setFormErrors({});
    setIsEdit(false);
    setAvatarImage("");
    setLogo(null);
    setIntegratorLogo(null);
    setIntegratorLogoUrl(null);
    setIntegratorLogoLoading(false);
  };

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  useEffect(() => {
    if (error && error !== "") {
      setFormErrors({});
      error &&
        !error.includes("Network Error") &&
        Object.keys(error).forEach((key) => {
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            name: key.message,
          }));
        });
    } else {
      setFormErrors({});
    }
  }, [error]);

  useEffect(() => {
    if (allClients && allClients.length > 0) {
      if (isCreateNewRow) {
        setData(
          allClients.sort((a, b) => {
            return b.isFlagged - a.isFlagged;
          })
        );
        setIsCreateNewRow(false);
      } else {
        setData((prevData) => {
          const newMap = new Map(allClients.map((item) => [item.id, item]));

          const updatedPrevData = prevData.map((item) =>
            newMap.has(item.id) ? newMap.get(item.id) : item
          );

          const prevDataIds = new Set(prevData.map((item) => item.id));
          const newItems = allClients.filter(
            (shift) => !prevDataIds.has(shift.id)
          );

          return [...updatedPrevData, ...newItems]
            .filter((item) => item.id !== deleteId)
            .sort((a, b) => b.isFlagged - a.isFlagged);
        });
      }
    } else {
      setData([]);
      setIsEdit(false);
    }
  }, [allClients]);

  const handleCancelAddRow = () => {
    setIsCreateNewRow(false);
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [pageNum, perPageItems]);

  const fetchClientData = async () => {
    setIsLoading(true);
    service
      .fetchSingleClientDataRequest(selectedRow.id)
      .then((clientData) => {
        setName(clientData.name);
        setRefId(clientData.refId);
        setIndustries(clientData.industries || []);
        setManager(clientData.manager);
        setManagerId(clientData.manager?.id);
        setMgntTeam(clientData.mgntTeam);
        setMgntTeamIds(
          clientData.mgntTeam?.length > 0
            ? clientData.mgntTeam.map((contact) => contact.id).join(",")
            : ""
        );
        setCoreTeam(clientData.coreTeam);
        setCoreTeamIds(
          clientData.coreTeam?.length > 0
            ? clientData.coreTeam.map((contact) => contact.id).join(",")
            : ""
        );
        setBusinessUnit(clientData.businessUnit);
        setContractStartDate(clientData.contractStartDate);
        setContractEndDate(clientData.contractEndDate);
        setFocal(clientData.focal);
        setCountry(clientData.country);
        setCity(clientData.city);
        setStatus(clientData.status);
        setStateProvince(clientData.stateProvince);
        setPostalCode(clientData.postalCode ? clientData.postalCode : "");
        setAddress(clientData.address);
        setNotes(clientData.notes);
        setAvatarImage(clientData.logoUrl);
        setIntegratorLogoUrl(clientData.integratorLogoUrl);
        setAttachments(clientData.attachments);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("Error fetching user data:", error);
      });
  };

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      isEdit && fetchClientData();
    } else {
      setName("");
      setRefId("");
      setIndustries([]);
      setManager(null);
      setManagerId("");
      setMgntTeam([]);
      setMgntTeamIds("");
      setCoreTeam([]);
      setCoreTeamIds("");
      setBusinessUnit("");
      setContractStartDate("");
      setContractEndDate("");
      setFocal("");
      setCountry("");
      setCity("");
      setStateProvince("");
      setPostalCode("");
      setAddress("");
      setNotes("");
      setAvatarImage(null);
      setLogo(null);
      setIntegratorLogo(null);
      setIntegratorLogoUrl(null);
      setIntegratorLogoLoading(false);
    }
  }, [selectedRow, isEdit]);

  const handleCreate = () => {
    setIsCreate(true);
  };

  useEffect(() => {
    if (name) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        name: "",
      }));
    }
    if (managerId) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        managerId: "",
      }));
    }
  }, [managerId, name]);

  const [isDisable, setIsDisable] = useState(false);

  useEffect(() => {
    if (modalLoading) setIsDisable(true);
    else setIsDisable(false);
  }, [modalLoading]);

  const handleSearchTrigger = (searchTerm) => {
    if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

    let pageNumber = pageNum;
    if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
      setPageNum(1);
    }

    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);

  };

  return (
    <>
      <ColumnConfigProvider tableName="Client">
        <div className="listingScreen position-relative">
          <div className="db_Subheader d-flex justify-content-between align-items-center mb-4">
            <h4>Clients</h4>
            {permissions?.includes("Client.Create") && (
              <Button
                btnClass="d_btn d_btn_primary"
                icon={<AddIconSvg />}
                label="Add Client"
                clickHandler={handleCreate}
              />
            )}
          </div>
        </div>
        <Table
          type="Client"
          rows={data || []}
          searchHandler={handleSearch}
          body={formBody()}
          saveBtnLabel="Save"
          cancelBtnLabel="Cancel"
          setPageNum={setPageNum}
          perPageItems={perPageItems}
          setIsCreate={setIsCreate}
          handleSave={() => handleSave()}
          handleDelete={(selectedRow) => handleDelete(selectedRow)}
          handleCancel={() => handleCancel()}
          setIsEdit={setIsEdit}
          setData={setData}
          loading={pageLoading}
          modalLoading={isModalLoading}
          isCreate={isCreate}
          forceShowDeleteModal={deleteModal}
          pagination={pagination}
          setSelectedRow={setSelectedRow}
          handleAddRow={handleAddRow}
          handleCancelAddRow={handleCancelAddRow}
          triggerSearch={handleSearchTrigger}

        />
      </ColumnConfigProvider>
      {deleteRequestModal && (
        <DeleteRequest
          data={deleteRequestData}
          handleCancel={setDeleteRequestModal}
          handleRefresh={() => handleDeleteRequested()}
        />
      )}
    </>
  );
};

export default Clients;
